// Monolith — easter egg body tree.
//
// The black slab orbiting Jupiter is a tribute to Arthur C. Clarke's
// 2001: A Space Odyssey. Its proportions (1:4:9 — the squares of the
// first three integers) are the fictional signature of an artifact left
// by extraterrestrial intelligence. Clicking it opens this lore tree.

export const monolithTree = {
  id: 'monolith',
  label: 'TMA-1',
  type: 'Alien Artifact',
  summary: 'The black monolith orbiting Jupiter, a fictional artifact from 2001: A Space Odyssey.',
  payload: [
    {
      type: 'description',
      text: 'A perfectly proportioned black slab of unknown manufacture. Its dimensions are exactly 1 by 4 by 9, the squares of the first three positive integers. The proportions are claimed to continue at higher dimensions: the squares of 1, 2, 3, 4, 5, 6, 7, 8, 9 in successive orthogonal axes. No known physical process produces objects this exact, this featureless, or this dimensionally pure. Whatever made the monolith wanted us to know that it could.'
    },
    {
      type: 'callout',
      tone: 'info',
      text: 'The monolith is a fictional object from Arthur C. Clarke\'s 2001: A Space Odyssey (1968). This tree is the easter egg lore for the visualization. Click any leaf to dig deeper.'
    }
  ],
  children: [
    {
      id: 'proportions',
      label: 'The 1:4:9 ratio',
      summary: 'Why the dimensions are the signature, not the size',
      payload: [
        {
          type: 'description',
          text: 'The monolith\'s thickness, width, and height are in the exact ratio 1:4:9. These are the squares of 1, 2, and 3. Clarke chose the ratio precisely because it is mathematically impossible to manufacture by accident: any natural process would introduce some asymmetry, some chip, some deviation. A perfect 1:4:9 slab is a calling card. It says: "we know geometry; we wanted you to notice."'
        },
        {
          type: 'stats',
          label: 'Proportions',
          entries: [
            { label: 'Thickness',  value: '1 unit  (1²)' },
            { label: 'Width',      value: '4 units (2²)' },
            { label: 'Height',     value: '9 units (3²)' },
            { label: 'Surface ratio', value: '1 : 4 : 9 : 16 : 25 ... (claimed)' },
            { label: 'Tolerance',  value: 'Beyond measurement precision' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'In Clarke\'s 2001 novel, scientists at Tycho confirm the ratio holds to the limit of their instruments. The film by Kubrick shows the slab matter-of-factly; the audience is meant to feel what the apes felt: that something far older and far smarter has been here.'
        }
      ]
    },

    {
      id: 'tycho-excavation',
      label: 'TMA-1: Tycho excavation',
      summary: '1999: the first monolith found, in a crater on the Moon',
      payload: [
        {
          type: 'description',
          text: 'The first monolith encountered by humans was discovered in 1999, deliberately buried 12 meters beneath the lunar surface in the Tycho crater. It was christened TMA-1 ("Tycho Magnetic Anomaly 1") because it was first detected as a strong magnetic field reading. When the Sun first struck its surface after excavation, it transmitted a powerful radio burst aimed at Jupiter. Whatever placed it had been waiting for any species advanced enough to find it, dig it up, and let sunlight touch it.'
        },
        {
          type: 'list',
          entries: [
            { primary: '4 million years before excavation', secondary: 'Buried by unknown agency on the lunar surface' },
            { primary: '1999', secondary: 'Detected via magnetic anomaly. Excavation begins.' },
            { primary: 'First lunar dawn after excavation', secondary: 'Monolith transmits a powerful, narrow-beam radio signal directly at Jupiter' },
            { primary: '2001', secondary: 'Discovery One mission launched to investigate Jupiter (in book; Saturn in original Clarke draft)' }
          ]
        }
      ]
    },

    {
      id: 'jupiter-mission',
      label: 'Jupiter Mission (Discovery One)',
      summary: '2001: the journey to find what TMA-1 was calling',
      payload: [
        {
          type: 'description',
          text: 'After TMA-1 transmitted its signal toward Jupiter, NASA launched the Discovery One mission to investigate. The crew of five (three in hibernation and two awake, David Bowman and Frank Poole) was supervised by HAL 9000, the most sophisticated AI ever built. HAL had been given conflicting instructions about the mission\'s true purpose. The resulting paranoid loop killed Poole and the hibernating crew. Bowman survived, deactivated HAL, and continued alone.'
        },
        {
          type: 'stats',
          label: 'Mission parameters',
          entries: [
            { label: 'Spacecraft',  value: 'Discovery One' },
            { label: 'Crew',        value: '5 (Bowman, Poole, +3 in hibernation)' },
            { label: 'AI',          value: 'HAL 9000' },
            { label: 'Destination', value: 'Jupiter (book) / Saturn (early draft)' },
            { label: 'Outcome',     value: 'Bowman survived; encountered second monolith; "transformation"' }
          ]
        }
      ]
    },

    {
      id: 'tma-2',
      label: 'TMA-2: the Jupiter monolith',
      summary: 'Orbiting Jupiter: the gateway',
      payload: [
        {
          type: 'description',
          text: 'A second monolith, vastly larger than TMA-1, was discovered in orbit around Jupiter. Designated TMA-2, it was the actual recipient of the lunar monolith\'s signal. When David Bowman approached it in his EVA pod, the monolith\'s flat surface opened into a tunnel of light: "My God, it\'s full of stars." Bowman was transported across vast distances and transformed into the Star Child. The monolith was a stargate, a transmitter, a teaching machine, and ultimately a midwife for the next stage of evolution.'
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'In our simulation, this is the monolith you can see and click, orbiting Jupiter near Io. Its real-world equivalent is fiction; but the proportions are real.'
        }
      ]
    },

    {
      id: 'apes',
      label: 'The Dawn of Man',
      summary: '4 million years ago: the first contact',
      payload: [
        {
          type: 'description',
          text: 'In the opening of 2001, a tribe of australopithecine apes wakes one morning to find a monolith standing in their gathering place. Touching it, they begin to comprehend tools: first as defense, then as weapons. The monolith jump-started the evolutionary leap that would, four million years later, produce the species capable of finding TMA-1. The implication: humanity itself is a project of whatever placed the monoliths, and our intelligence is something they set in motion.'
        }
      ]
    },

    {
      id: 'creators',
      label: 'The unseen makers',
      summary: 'Who made the monoliths, and why',
      payload: [
        {
          type: 'description',
          text: 'Clarke deliberately never identified the makers in detail. In the novels they are ancient beings who left their biological bodies behind, first for machines and then for bodies of radiation. Their purpose appears to be cultivation: finding promising biological intelligence, nudging it forward at critical thresholds, watching what it becomes. The monoliths are their tools: tutors, transmitters, gates. The makers themselves are never seen. We are not their first project. We will not be their last.'
        }
      ]
    },

    {
      id: 'real-world',
      label: 'The real "1999 monolith"',
      summary: 'When fiction caught up with itself',
      payload: [
        {
          type: 'description',
          text: 'In November 2020, a metal monolith was discovered in the Utah desert by Department of Public Safety officers conducting a wildlife survey. Roughly 3 meters tall, polished triangular prism, no signage, planted firmly into the rock. It vanished within days. Similar monoliths appeared in Romania, California, the Isle of Wight, the Netherlands, and the Las Vegas mountains. None were claimed by any artist or agency. Clarke would have appreciated it: the universe imitating the fiction that was itself imitating the universe.'
        },
        {
          type: 'links',
          entries: [
            { label: 'Wikipedia: Monolith (Space Odyssey)', url: 'https://en.wikipedia.org/wiki/Monolith_(Space_Odyssey)' },
            { label: 'Wikipedia: 2020 monolith appearances', url: 'https://en.wikipedia.org/wiki/2020_monolith_appearances' },
            { label: '2001: A Space Odyssey (novel)', url: 'https://en.wikipedia.org/wiki/2001:_A_Space_Odyssey_(novel)' }
          ]
        }
      ]
    }
  ]
};
